import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { AccountService } from './account.service';

@Injectable()
export class Principal {
    private userIdentity: any;
    private userIdentityPromise: Promise<any>;
    private authenticated = false;
    private authenticationState = new Subject<any>();

    constructor(private account: AccountService) {}

    authenticate(identity) {
        this.userIdentity = identity;
        this.authenticated = identity !== null;
        this.authenticationState.next(this.userIdentity);
    }

    async hasNoneAuthorities(authorities: string[]): Promise<boolean> {
        await this.identity();
        if (
            !this.authenticated ||
            !this.userIdentity ||
            !this.userIdentity.authorities
        ) {
            return false;
        }
        for (const authority of authorities) {
            if (this.userIdentity.authorities.includes(authority)) {
                return false;
            }
        }
        return true;
    }

    async hasAnyAuthority(authorities: string[]): Promise<boolean> {
        await this.identity();
        if (
            !this.authenticated ||
            !this.userIdentity ||
            !this.userIdentity.authorities
        ) {
            return Promise.resolve(false);
        }

        for (let i = 0; i < authorities.length; i++) {
            if (
                authorities[i][0] !== '!' &&
                this.userIdentity.authorities.indexOf(authorities[i]) !== -1
            ) {
                return Promise.resolve(true);
            } else if (
                authorities[i][0] === '!' &&
                this.userIdentity.authorities.indexOf(
                    authorities[i].substr(1)
                ) === -1
            ) {
                return Promise.resolve(true);
            }
        }

        return Promise.resolve(false);
    }

    async hasAuthority(authority: string): Promise<boolean> {
        await this.identity();
        if (!this.authenticated) {
            return Promise.resolve(false);
        }

        if (authority[0] === '!') {
            return this.identity().then(
                (id) => {
                    return Promise.resolve(
                        id.authorities &&
                            id.authorities.indexOf(authority.substr(1)) === -1
                    );
                },
                () => {
                    return Promise.resolve(false);
                }
            );
        }

        return this.identity().then(
            (id) => {
                return Promise.resolve(
                    id.authorities && id.authorities.indexOf(authority) !== -1
                );
            },
            () => {
                return Promise.resolve(false);
            }
        );
    }

    identity(force?: boolean): Promise<any> {
        if (force === true) {
            this.userIdentity = undefined;
        }

        // check and see if we have retrieved the userIdentity data from the server.
        // if we have, reuse it by immediately resolving
        if (this.userIdentity) {
            return Promise.resolve(this.userIdentity);
        }
        if (this.userIdentityPromise) {
            return this.userIdentityPromise;
        }

        // retrieve the userIdentity data from the server, update the identity object, and then resolve.
        this.userIdentityPromise = this.account
            .get()
            .toPromise()
            .then((account) => {
                if (account) {
                    this.userIdentity = account;
                    this.authenticated = true;
                } else {
                    this.userIdentity = null;
                    this.authenticated = false;
                }
                this.authenticationState.next(this.userIdentity);
                return this.userIdentity;
            })
            .catch((err) => {
                this.userIdentity = null;
                this.authenticated = false;
                this.authenticationState.next(this.userIdentity);
                return null;
            });
        return this.userIdentityPromise;
    }

    isAuthenticated(): boolean {
        return this.authenticated;
    }

    isIdentityResolved(): boolean {
        return this.userIdentity !== undefined;
    }

    getAuthenticationState(): Observable<any> {
        return this.authenticationState.asObservable();
    }

    getImageUrl(): String {
        return this.isIdentityResolved() ? this.userIdentity.imageUrl : null;
    }
}



// WEBPACK FOOTER //
// ./src/app/shared/auth/principal.service.ts